import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigurationService } from '../_services/configuration.service';
import { UserProfileService } from '../_services/user-profile.service';
import { UserProfile } from '../_models/user-profile';
import { RunTicket, JSONPatchDocs } from '../_models/field';
import { Observable } from 'rxjs';
import { CloudFile } from '../_models/files';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8',
    'Access-Control-Allow-Origin': '*',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  })
};

const jsonPatchDocsOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json-patch+json'
  })
};

@Injectable({
  providedIn: 'root'
})
export class PortalApiService {
  private apiAddress: string = '';
  public token: string;
  userProfile: UserProfile;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private userService: UserProfileService
  ) {
    this.apiAddress = this.configurationService.config.apiAddress;
    this.configurationService.configData$.subscribe(res => {
      this.apiAddress = res.apiAddress;
    });

    this.userService.userProfile$.subscribe(currentProfile => {
      this.userProfile = currentProfile;
    });
  }

  getReports() {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Users/Reports', httpOptions);
  }

  getFields() {
    return this.http.get(this.apiAddress + 'api/Fields/Company/' + this.userProfile.currentCompany, httpOptions);
  }

  getFieldDataSheets(fieldID: number, startDate: string, endDate: string) {
    return this.http.get(this.apiAddress + 'api/Fields/' + fieldID + '/Company/' + this.userProfile.currentCompany + '?startDate=' + startDate + '&endDate=' + endDate, httpOptions);
  }

  getFieldBatteries(fieldID: number) {
    return this.http.get(this.apiAddress + 'api/Batteries/Company/' + this.userProfile.currentCompany + '?fieldID=' + fieldID, httpOptions);
  }

  getFieldTanks(fieldID: number, batteryID: number): Observable<any> {
    return this.http.get(this.apiAddress + 'api/Fields/' + fieldID + '/TanksDataEntry?companyName=' + this.userProfile.currentCompany + '&batteryID=' + batteryID, httpOptions);
  }

  getFieldRunTickets(fieldID: number, startDate: string, endDate: string) {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/RunTickets?startDate=' + startDate + '&endDate=' + endDate + '&fieldID=' + fieldID, httpOptions);
  }

  /* Run Ticket */
  addRunTicket(tankID: number, entryDate: string, newRunTicket: RunTicket) {
    return this.http.post(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/RunTickets/Tanks/' + tankID + '?entryDate=' + entryDate, newRunTicket, httpOptions);
  }

  updateRunTicket(tankID: number, entryDate: string, updatedTicket: JSONPatchDocs[]) {
    return this.http.patch(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/RunTickets/Tanks/' + tankID + '?entryDate=' + entryDate, updatedTicket, jsonPatchDocsOptions);
  }

  updateRunTicketDate(tankID: number, entryDate: string, newDate: string, ticketNum: string) {
    return this.http.patch(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/RunTickets/Tanks/' + tankID + '/Date?entryDate=' + entryDate + '&newDate=' + newDate, JSON.stringify(ticketNum), httpOptions);
  }

  removeRunTicket(tankID: number, entryDate: string, ticketNum: string) {
    return this.http.delete(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/RunTickets/Tanks/' + tankID + '?entryDate=' + entryDate, { body: JSON.stringify(ticketNum), headers: httpOptions.headers });
  }

  getTankVolFromHeight(tankID: number, entryDate: string, height: number) {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/RunTickets/Tanks/' + tankID + '/Volume?entryDate=' + entryDate + '&height=' + height, httpOptions);
  }

  /* Layout */
  getCompanyLayout(fieldID: number, wellID: number = 0) {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Layouts/RunTicket?fieldID=' + fieldID + '&wellID=' + wellID, httpOptions);
  }

  /* Add to Favorites */
  addReportToFavorites(reportID: number) {
    return this.http.put(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Users/Reports/' + reportID + '/Favorite/Add', httpOptions);
  }

  removeReportFromFavorites(reportID: number) {
    return this.http.put(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Users/Reports/' + reportID + '/Favorite/Remove', httpOptions);
  }

  addFieldToFavorites(fieldID: number) {
    return this.http.put(this.apiAddress + 'api/Fields/Companies/' + this.userProfile.currentCompany + '/Users/' + fieldID + '/Favorite/Add', httpOptions);
  }

  removeFieldFromFavorites(fieldID: number) {
    return this.http.put(this.apiAddress + 'api/Fields/Companies/' + this.userProfile.currentCompany + '/Users/' + fieldID + '/Favorite/Remove', httpOptions);
  }

  getDashboards() {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Users/Reports/Dashboards', httpOptions);
  }

  getDashboardAutorization(dashboardID: string) {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Users/Reports/Dashboards/' + dashboardID, httpOptions)
  }

  downloadRunTicketTemplate(templateType: string, fieldID: number) {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Layouts/Templates?templateType=' + templateType + '&fieldID=' + fieldID, { responseType: "blob" });
  }

  importRunTickets(runTicketFile: any) {
    return this.http.post(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/RunTickets/File', runTicketFile);
  }

  getFieldList(includeWells: boolean) {
    return this.http.get(this.apiAddress + 'api/Companies/' + this.userProfile.currentCompany + '/Jobs/Fields?includeWells=' + includeWells, httpOptions);
  }

  getFileListForWell(wellID: number) {
    return this.http.get(`${this.apiAddress}api/Companies/${this.userProfile.currentCompany}/Storage/Wells/${wellID}/Files`, httpOptions);
  }

  downloadFileForWell(wellID: number, fileName: string) {
    let encodedFileName: string = encodeURIComponent(fileName);
    return this.http.get(`${this.apiAddress}api/Companies/${this.userProfile.currentCompany}/Storage/Wells/${wellID}/File?fileName=${encodedFileName}`, { responseType: "blob" });
  }

  addFileForWell(wellID: number, fileToAdd: any) {
    return this.http.post(`${this.apiAddress}api/Companies/${this.userProfile.currentCompany}/Storage/Wells/${wellID}/File`, fileToAdd);
  }

  updateFileDataForWell(wellID: number, fileName: string, fileData: CloudFile) {
    let encodedFileName: string = encodeURIComponent(fileName);
    return this.http.put(`${this.apiAddress}api/Companies/${this.userProfile.currentCompany}/Storage/Wells/${wellID}/File?fileName=${encodedFileName}`, fileData, httpOptions);
  }

  deleteFileForWell(wellID: number, fileName: string) {
    let encodedFileName: string = encodeURIComponent(fileName);
    return this.http.delete(`${this.apiAddress}api/Companies/${this.userProfile.currentCompany}/Storage/Wells/${wellID}/File?fileName=${encodedFileName}`, httpOptions);
  }
}
